import {ModalComponentProps, Card, Block, Dialog, DialogBody, DialogFooter, Button, List, ListItem, Space, Icon, Text, Tag} from '@mc/ui-v4';
import React, {useMemo} from 'react';
import {useQuery} from 'react-query';
import {LegendItemVO, ServicesApi, LayerVO} from '../../api';
import {LegendItemType} from '../../api/gen/model/legend-item-type';
import {WaitForQueryData} from '@mc/react-app-v4';
import {useLayersConfig} from '../../hooks/useLayersConfig';
import {getWmsWmtsLayerIds} from '../../helpers/LayerConfigUtils';
import {LayerId} from '@mc/react-mapio';
import {mcSearch} from '@mc/react-ui-icons';
import {FormattedLegend} from './FormattedLegend';
import './LegendModal.scss';

type Props = ModalComponentProps & {
    className?: string;
    layerIds: LayerId[];
    searchableAttributes?: {[key: string]: string[]};
    /** Přednačtená konfigurace vrstev (z preloadu). Pokud chybí, načte se interním dotazem. */
    layersConfig?: {[key: string]: LayerVO};
    /** Přednačtené legendy WMS/WMTS vrstev (z preloadu). Pokud chybí, načtou se interním dotazem. */
    layerLegends?: {[key: string]: LegendItemVO};
    /** Formátovaná legenda tématu (HTML z WYSIWYG editoru) zobrazená nad seznamem vrstev. */
    formattedLegend?: string;
};

type LegendTreeItem = LegendItemVO & {
    markerUrl?: string;
    layerId?: number;
    /** Popisky atributů, podle kterých lze ve vrstvě fulltextově vyhledávat. */
    searchableAttributes?: string[];
};

const colorArrayToCss = (color?: number[]): string => {
    if (!color || color.length < 3) return '#000000';
    const [r, g, b] = color;
    return `rgb(${r}, ${g}, ${b})`;
};

const LegendItemIcon: React.FC<{item: LegendTreeItem; color: string}> = ({item, color}) => {
    if (item.type === LegendItemType.IMAGE && item.image) {
        return <img src={item.image} alt={item.label} className="legend-item-icon-image" />;
    }

    if (item.type === LegendItemType.POINT) {
        if (item.markerUrl) {
            return (
                <img
                    src={globalThis.serverContextPath + '/api/files/img/' + item.markerUrl}
                    alt={item.label}
                    className="legend-item-icon-marker"
                    onError={event => {
                        (event.target as HTMLImageElement).style.display = 'none';
                    }}
                />
            );
        }
        return <div className="legend-item-icon-point" style={{backgroundColor: color}} />;
    }

    if (item.type === LegendItemType.LINE) {
        return <div className="legend-item-icon-line" style={{borderBottomColor: color}} />;
    }

    if (item.type === LegendItemType.POLYGON) {
        return (
            <div className="legend-item-icon-polygon">
                <div className="legend-item-icon-polygon-border" style={{borderBottomColor: color}} />
                <div className="legend-item-icon-polygon-fill-1" />
                <div className="legend-item-icon-polygon-fill-2" style={{borderBottomColor: color}} />
            </div>
        );
    }

    return null;
};

LegendItemIcon.displayName = 'LegendItemIcon';

export const LegendModal: React.FC<Props> = ({className, onClose, layerIds, searchableAttributes, layersConfig, layerLegends, formattedLegend}) => {
    const layersConfigQuery = useLayersConfig(layersConfig);

    // Data buď z preloadu (props), nebo z interních dotazů (fallback).
    const layersConfigData = layersConfig ?? layersConfigQuery.data;

    const wmsWmtsLayerIds = useMemo(() => getWmsWmtsLayerIds(layersConfigData, layerIds), [layerIds, layersConfigData]);

    const layerLegendsQuery = useQuery<{[key: string]: LegendItemVO}>(
        ['CACHE_KEY_LAYER_LEGENDS', wmsWmtsLayerIds],
        async () => {
            if (wmsWmtsLayerIds.length === 0) {
                return {};
            }

            return (await ServicesApi.getLayerLegends(wmsWmtsLayerIds)).data;
        },
        {
            // Pokud máme legendy z preloadu, interní dotaz nespouštíme a použijeme je jako initialData.
            enabled: !layerLegends && wmsWmtsLayerIds.length > 0,
            initialData: layerLegends,
            refetchOnMount: false,
            refetchOnWindowFocus: false,
            refetchOnReconnect: false,
        },
    );

    // Položky legendy odvozujeme přímo z dat (bez stavu/efektu) – WMS/WMTS legendy z dotazu
    // doplněné o vektorové vrstvy z konfigurace.
    const legendItems = useMemo<LegendTreeItem[]>(() => {
        if (!layersConfigData) {
            return [];
        }

        const items: LegendTreeItem[] = [];

        if (layerLegendsQuery.data) {
            Object.entries(layerLegendsQuery.data).forEach(([layerId, item]) => {
                const layer = layersConfigData[layerId];
                const markerUrl = item.type === LegendItemType.POINT ? layer?.style?.marker || layer?.layerImage : undefined;
                items.push({...item, markerUrl, layerId: Number(layerId), searchableAttributes: searchableAttributes?.[layerId]});
            });
        }

        layerIds.forEach(id => {
            const layer = layersConfigData[id.toString()];
            if (layer && layer.type !== 'wms' && layer.type !== 'wmts' && !items.some(item => item.label === layer.label)) {
                const isPointLayer = layer.allowedGeometry?.includes('POINT');
                items.push({
                    label: layer.label,
                    type: isPointLayer ? LegendItemType.POINT : LegendItemType.LINE,
                    color: layer.style?.color || layer.iconColor || [100, 100, 100],
                    disabled: false,
                    markerUrl: isPointLayer ? layer.style?.marker || layer.layerImage : undefined,
                    layerId: layer.id,
                    searchableAttributes: layer.id != null ? searchableAttributes?.[String(layer.id)] : undefined,
                });
            }
        });

        return items;
    }, [layerLegendsQuery.data, searchableAttributes, layersConfigData, layerIds]);

    return (
        <Dialog portalClassName={className} size={'xl'} onClose={onClose} title={'Legenda'}>
            <DialogBody>
                {formattedLegend && (
                    <Card mode={'subsection'} className={'legend-formatted-card'}>
                        <Block>
                            <FormattedLegend html={formattedLegend} />
                        </Block>
                    </Card>
                )}
                <Card mode={'subsection'}>
                    <Block>
                        <WaitForQueryData queries={[layerLegendsQuery, layersConfigQuery]}>
                            {() => {
                                if (legendItems.length === 0) {
                                    return <Text>Není k dispozici legenda pro žádnou z viditelných vrstev.</Text>;
                                }

                                return (
                                    <List className="legend-list">
                                        {legendItems.map((item, idx) => (
                                            <ListItem
                                                key={`${item.label}-${idx}`}
                                                className="legend-item"
                                                extra={
                                                    <Space className="legend-item-content">
                                                        <div className="legend-item-icon">
                                                            <LegendItemIcon item={item} color={colorArrayToCss(item.color)} />
                                                        </div>
                                                        <div className="legend-item-main">
                                                            <span className="legend-item-label">{item.label}</span>
                                                            {item.searchableAttributes && item.searchableAttributes.length > 0 && (
                                                                <div className="legend-item-searchable">
                                                                    <Icon icon={mcSearch} size={'sm'} intent={'primary'} />
                                                                    <Text intent={'secondary'} className="legend-item-searchable-label">
                                                                        Lze hledat podle:
                                                                    </Text>
                                                                    {item.searchableAttributes.map((attr, attrIdx) => (
                                                                        <Tag key={`${attr}-${attrIdx}`} intent={'primary'} type={'outline'}>
                                                                            {attr}
                                                                        </Tag>
                                                                    ))}
                                                                </div>
                                                            )}
                                                        </div>
                                                    </Space>
                                                }
                                            />
                                        ))}
                                    </List>
                                );
                            }}
                        </WaitForQueryData>
                    </Block>
                </Card>
            </DialogBody>
            <DialogFooter>
                <Button style={'link'} onClick={onClose}>
                    Zavřít
                </Button>
            </DialogFooter>
        </Dialog>
    );
};
